import type { NotificationView } from './types';

/** One line of the collapsed aggregate: a backend-provided bucket key + its count. */
export interface NotificationSummarySegment {
  /** Key relative to `notifications.*` — rendered with `t(key, { count })`. */
  key: string;
  count: number;
}

/**
 * Aggregate notifications into ordered count buckets for the collapsed strip. Bucketing and order
 * are backend-defined (`summaryKey` / `summaryOrder`); the client only groups + counts. Unknown
 * orders sort last, with an alphabetical tiebreak for determinism.
 */
export function summarizeNotifications(
  activities: readonly NotificationView[]
): NotificationSummarySegment[] {
  const counts = new Map<string, number>();
  const order = new Map<string, number>();

  for (const activity of activities) {
    if (!activity.summaryKey) continue;
    counts.set(activity.summaryKey, (counts.get(activity.summaryKey) ?? 0) + 1);
    if (!order.has(activity.summaryKey)) {
      order.set(activity.summaryKey, activity.summaryOrder ?? Number.MAX_SAFE_INTEGER);
    }
  }

  return [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => order.get(a.key)! - order.get(b.key)! || a.key.localeCompare(b.key));
}
